import { useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import wait from '@helpers/wait';
import { useResponsive } from '@hooks/useResponsive';
import { useAssetMVPermissions } from '@hooks';

const event = new MouseEvent('click', {
  bubbles: true,
  cancelable: true,
  view: window
});

export default function useMediaViewerAnnouncementTour({
  isAssetReady,
  isPreparing,
  isUnsupported
}: {
  isAssetReady: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
}) {
  const { isLgWidth } = useResponsive();
  const { onStartTour, onCompleteStep } = useOnboardingProcess();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const startTour = searchParams.get('startTour');
  const isEmbeddedWebView = searchParams.get('embedded') === '1';
  const canLeaveComment = useAssetMVPermissions('comment');
  const hasDecisionStep = !location.pathname.includes('external-reviewer');

  const onOpenDropdownMenu = useCallback(async (query: string) => {
    const commentHtml = document.querySelector(query);
    commentHtml?.dispatchEvent(event);
    await wait(800);
  }, []);

  const onStartTourAnnouncement = useCallback(() => {
    if (!isLgWidth) {
      onStartTour({
        type: 'announcement-media-viewer-mobile',
        onClickNext: (currentStep) => {
          if (currentStep === 2 && !hasDecisionStep) {
            onCompleteStep({
              type: 'announcement-media-viewer-mobile',
              hideCongrats: true
            });
            onStartTour({ type: undefined });
            searchParams.delete('startTour');
            setSearchParams(searchParams, { replace: true });
          }
        }
      });
      return;
    }

    onStartTour({
      type: 'announcement-media-viewer',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1) {
          await wait(1000);
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
          return;
        }
        if (currentStep === 2) {
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-versions-btn'
          );
        }
      },
      onClickPrev: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 3) {
          await wait(1000);
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
        }
      }
    });
  }, [
    onStartTour,
    onCompleteStep,
    isLgWidth,
    hasDecisionStep,
    onOpenDropdownMenu,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    if (isEmbeddedWebView) return;
    if (
      ![
        'announcement-media-viewer',
        'announcement-media-viewer-mobile'
      ].includes(startTour || '')
    )
      return;
    if (!canLeaveComment) return;
    if (isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTourAnnouncement();
  }, [
    isEmbeddedWebView,
    startTour,
    canLeaveComment,
    isAssetReady,
    isPreparing,
    isUnsupported,
    onStartTourAnnouncement
  ]);

  return null;
}
